import { yupResolver } from "@hookform/resolvers/yup";
import type { FC } from "react";
import { Controller, useForm } from "react-hook-form";

import type { AuthFeatureData } from "~/core";
import { createRestoreValidationSchema } from "~/features/restore-password-form/restore.schema";
import { Button } from "~/shared/ui-kit";
import { Card } from "~/shared/ui-kit/card/Card";
import { Flex } from "~/shared/ui-kit/flex/Flex";
import { TextField } from "~/shared/ui-kit/text-field/TextField";
import { Title } from "~/shared/ui-kit/title/Title";
import { Typography } from "~/shared/ui-kit/typography/Typography";
import type { RestoreCredential } from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";

export type ServerErrors = string;

export type RestoreData = ExtractArrayItemType<
  NonNullable<AuthFeatureData>["restore_password"]
>;

export type RestorePasswordFormProps = {
  data: RestoreData;
  serverResponse?: ServerErrors;
  onSubmit: (data: RestoreCredential) => void;
};

export const RestorePasswordForm: FC<RestorePasswordFormProps> = (props) => {
  const { serverResponse, data, onSubmit } = props;

  const { handleSubmit, control } = useForm<RestoreCredential>({
    resolver: yupResolver(
      createRestoreValidationSchema({
        validation_required: data?.validation_required ?? "Field is required!",
        validation_invalid: data?.validation_invalid ?? "Email is invalid",
      }),
    ),
  });

  return (
    <Card
      variant="secondary"
      className="min-w-[312px] md:w-[524px] min-h-[572px] md:min-h-[640px] rounded-2xl md:rounded-lg"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 md:px-10 py-6 md:py-8 flex-grow flex flex-col w-full"
      >
        <Title
          as="h2"
          size="small"
          weight="bold"
          className="text-center md:text-start pb-4 md:pb-6 m-0"
        >
          {data?.form_title}
        </Title>

        <Flex gap={8} flexDirection="column">
          <Typography size="large" className="text-center md:text-start">
            {data?.form_description ?? "Please enter your email"}
          </Typography>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={data?.form_email_input_label ?? "Email"}
                errorMessage={error?.message ?? serverResponse}
              />
            )}
          />
        </Flex>

        <Button
          type="submit"
          size="small"
          md={{ size: "medium" }}
          weight="semibold"
          className="md:py-4 py-3 mt-auto w-full"
        >
          {data?.form_submit_button_text}
        </Button>
      </form>
    </Card>
  );
};
